/**
 * Pendaftaran kedai dan pensijilan automatik peranti pertama.
 *
 * Sebab pensijilan automatik: aliran manual (daftar di terminal → log masuk → cipta kod pairing →
 * taip kod) mempunyai empat titik di mana pemilik boleh tersekat, dan semuanya sebelum dia melihat
 * satu pun produknya. Selepas pendaftaran, peranti ini terus menjadi peranti #1.
 */

export interface ShopOutlet {
  id: string;
  code: string;
  name: string;
}

export interface RegisteredShop {
  token: string;
  tenantName: string;
  tenantSlug: string;
  outlets: ShopOutlet[];
}

export class RegisterError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Hasilkan slug daripada nama kedai.
 *
 * Peraturannya sama dengan pelayan: huruf kecil, angka dan sengkang, 3–40 aksara. Kita menormalkan
 * di sini supaya pemilik tidak perlu memikirkan format — dia menaip "Kedai Ikan Hiasan 72" dan
 * mendapat `kedai-ikan-hiasan-72`.
 */
export function slugify(name: string): string {
  const base = name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');

  if (base.length >= 3) return base;
  // Nama pendek seperti "72" atau "A" — tambah akhiran supaya lulus pengesahan pelayan.
  return `${base}-kedai`.replace(/^-/, '').slice(0, 40);
}

async function failure(response: Response): Promise<RegisterError> {
  try {
    const body = (await response.json()) as { error?: { code?: string; message?: string } };
    return new RegisterError(
      response.status,
      body.error?.code ?? `HTTP_${response.status}`,
      body.error?.message ?? 'pendaftaran gagal',
    );
  } catch {
    return new RegisterError(response.status, `HTTP_${response.status}`, 'pendaftaran gagal');
  }
}

export interface RegisterInput {
  shopName: string;
  ownerName: string;
  email: string;
  password: string;
}

export async function registerShop(
  apiBase: string,
  input: RegisterInput,
  fetchImpl: typeof fetch = fetch,
): Promise<RegisteredShop> {
  const response = await fetchImpl(`${apiBase}/v1/auth/register`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      shop_name: input.shopName,
      shop_slug: slugify(input.shopName),
      name: input.ownerName,
      email: input.email,
      password: input.password,
    }),
  });

  if (!response.ok) throw await failure(response);

  const body = (await response.json()) as {
    access_token: string;
    tenant?: { name?: string; slug?: string };
    outlets?: Array<{ id: string; code: string; name: string }>;
  };

  if (typeof body.access_token !== 'string' || body.access_token === '') {
    throw new RegisterError(500, 'NO_TOKEN', 'Pelayan tidak memulangkan sesi');
  }

  return {
    token: body.access_token,
    tenantName: body.tenant?.name ?? input.shopName,
    tenantSlug: body.tenant?.slug ?? slugify(input.shopName),
    outlets: body.outlets ?? [],
  };
}

export interface PairingCode {
  code: string;
  expiresAt: string | null;
}

export async function createPairingCode(
  apiBase: string,
  token: string,
  input: { outletId: string; name: string; deviceNo: number },
  fetchImpl: typeof fetch = fetch,
): Promise<PairingCode> {
  const response = await fetchImpl(`${apiBase}/v1/devices/pairing-codes`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({
      outlet_id: input.outletId,
      name: input.name,
      device_no: input.deviceNo,
    }),
  });

  if (!response.ok) throw await failure(response);

  const body = (await response.json()) as { code: string; expires_at?: string };
  return { code: body.code, expiresAt: body.expires_at ?? null };
}
